import React, {useState, useEffect, useMemo, useContext, useRef} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {WebsiteContext} from "../../../context/WebsiteProvider";
import {UserContext} from "../../../context/UserProvider";
import {gsap} from "gsap";
import TetrisModal from "../../../components/modals/TetrisModal";
import WebsiteModal from "../../../components/modals/WebsiteModal";
import {FixedSizeList as List} from "react-window";
import ExportModal from "../../../components/modals/ExportModal";
import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import exportIcon from "../../../assets/icons/export.svg";
import { LuImport } from "react-icons/lu";
import recip from "../../../assets/icons/recip.png";
import {ReactComponent as FilterIcon} from "../../../assets/icons/filter.svg";
import {FaExclamationCircle, FaSpinner, FaBan} from "react-icons/fa";
import {MdFilterAltOff} from "react-icons/md";

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null && sortConfig.direction !== null) {
      sortableItems.sort((a, b) => {
        const { key, direction } = sortConfig;
        const aValue = a[key] ? a[key].toString().toLowerCase() : "";
        const bValue = b[key] ? b[key].toString().toLowerCase() : "";
        if (aValue < bValue) return direction === "ascending" ? -1 : 1;
        if (aValue > bValue) return direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";

    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const iconConfigurations = {
  documentsWaiting: {
    icon: (
      <FaExclamationCircle
        className="doc-waiting-icon text-orange-400 dark:text-yellow-400 h-4 w-4 mr-2"
      />
    ),
    label: "Document Waiting",
  },
  voidedDocuments: {
    icon: (
      <FaBan
        className="doc-voided-icon text-red-400 dark:text-red-400 h-4 w-4 mr-2"
      />
    ),
    label: "Document Voided",
  },
  hasReciprocal: {
    icon: (
      <img
        src={recip}
        alt="Has Reciprocal"
        className="inline h-4 w-4 mr-2 recip"
      />
    ),
    label: "Reciprocal",
  },
};

const WebsiteTable = ({data, onSwitchToImports}) => {
  const {
    setPage,
    getWebsite,
    getAllWebsites,
    loading,
    isOpen,
    openModal,
    closeModal,
    headerToKeyMapping,
    selectedWebsite,
    setSelectedWebsite,
  } = useContext(WebsiteContext);

  const {
    hideInActive,
    setHideInActive,
    hideDenied,
    setHideDenied,
    setUserPreference,
    websiteColumnVisibility,
    darkMode,
    role,
  } = useContext(UserContext);

  const location = useLocation();
  const navigate = useNavigate();
  const [showTetrisModal, setShowTetrisModal] = useState(false);
  const [showRickRoll, setShowRickRoll] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [checkboxStatus, setCheckboxStatus] = useState({});
  const loaderRef = useRef(null);
  const textRef = useRef(null);
  const legendRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dynamicHeight, setDynamicHeight] = useState(window.innerHeight - 210);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const doubleClickTimeout = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    vendor: true,
    domainName: true,
    licensee: true,
    status: true,
    firstName: true,
    lastName: true,
    mlsStatus: true,
    office: true,
    mlsId: true,
    memberNumber: true,
    licenseNumber: true,
    startDate: true,
    termDate: true,
    vendorCode: true,
    reciprocal: true,
    dba: true,
    memberAOR: true,
    broker: {
      status: true,
      firstName: true,
      lastName: true,
      memberNumber: true,
      licenseNumber: true,
      mlsId: true,
      office: true,
      officeId: true,
    },
  });
  const [startDate, setStartDate] = useState("");
  const [termDate, setEndDate] = useState("");
  const [showLegend, setShowLegend] = useState(false);
  const exportKeyLabels = {
    vendor: "Vendor",
    domainName: "Domain Name",
    licensee: "Licensee",
    firstName: "First Name",
    lastName: "Last Name",
    status: "Status",
    mlsStatus: "MLS Status",
    office: "Office",
    mlsId: "MLS ID",
    memberNumber: "Member Number",
    licenseNumber: "License Number",
    startDate: "Start Date",
    termDate: "Term Date",
    vendorCode: "Vendor Code",
    reciprocal: "Reciprocal",
    dba: "DBA",
    memberAOR: "Member AOR",
    broker: {
      firstName: "Broker First Name",
      lastName: "Broker Last Name",
      memberNumber: "Broker Member Number",
      licenseNumber: "Broker License Number",
      mlsId: "Broker MLS ID",
      office: "Broker Office",
      officeId: "Broker Office ID",
      status: "Broker Status",
    },
  };
  const [iconFilters, setIconFilters] = useState({
    actionNeeded: false,
    documentsWaiting: false,
    voidedDocuments: false,
    hasReciprocal: false,
    reciprocalAllowed: false,
    masterLinkedVendor: false,
    linkedVendor: false,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const toggleLegend = () => {
    setShowLegend((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        legendRef.current &&
        !legendRef.current.contains(event.target) &&
        showLegend
      ) {
        setShowLegend(false);
        triggerDoubleClickRequirement();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLegend]);

  useEffect(() => {
    if (openDropdownIndex === null) {
      triggerDoubleClickRequirement();
    }
  }, [openDropdownIndex]);

  const triggerDoubleClickRequirement = () => {
    if (doubleClickTimeout.current === null) {
      doubleClickTimeout.current = setTimeout(() => {
        doubleClickTimeout.current = null;
      }, 100);
    }
  };

  const toggleFilter = (filterKey) => {
    console.log("Setting Icon Filter for " + filterKey);
    let newFilter = {...iconFilters};
    Object.keys(iconFilters).forEach((key) => {
      newFilter[key] = key === filterKey ? !iconFilters[key] : iconFilters[key];
    });
    setIconFilters(newFilter);
  };

  const defaultColumns = [
    {columnName: "vendor", visibility: true, width: 250},
    {columnName: "domainName", visibility: true, width: 250},
    {columnName: "licensee", visibility: true, width: 170},
    {columnName: "Status", visibility: true, width: 200},
    {columnName: "office", visibility: true, width: 200},
    {columnName: "mlsId", visibility: true, width: 160},
    {columnName: "memberNumber", visibility: true, width: 160},
    {columnName: "mlsStatus", visibility: true, width: 160},
    {columnName: "startDate", visibility: true, width: 160},
    {columnName: "termDate", visibility: true, width: 160},
  ];

  const [columns, setColumns] = useState(() => {
    const visibility = Array.isArray(websiteColumnVisibility) ? websiteColumnVisibility : defaultColumns;
    return visibility.map(col => ({
      header: col.columnName === "Status" ? "Status" : col.columnName.charAt(0).toUpperCase() + col.columnName.slice(1),
      field: col.columnName,
      width: col.width || 160,
      ...(col.columnName === "Status" && {
        filter: true,
        filterOptions: ["Pending Activation", "Pending Termination", "Pending Change", "Active", "Inactive"].filter(Boolean)
      }),
      ...(col.columnName === "mlsStatus" && {filter: true, filterOptions: ["Active", "Q Suspension", "Terminated"]}),
    }));
  });

  const [columnVisibility, setColumnVisibility] = useState(() =>
    Array.isArray(websiteColumnVisibility) ? websiteColumnVisibility : defaultColumns
  );

  useEffect(() => {
    setColumns(prevColumns =>
      websiteColumnVisibility.map(col => {
        const existingCol = prevColumns.find(c => c.field === col.columnName) || {};
        return {
          ...existingCol,
          header: col.columnName === "Status" ? "Status" : col.columnName.charAt(0).toUpperCase() + col.columnName.slice(1),
          field: col.columnName,
          width: col.width || existingCol.width || 160,
          ...(col.columnName === "Status" && {
            filter: true,
            filterOptions: ["Pending Activation", "Pending Termination", "Pending Change", "Active", "Inactive"].filter(Boolean)
          }),
          ...(col.columnName === "mlsStatus" && {
            filter: true,
            filterOptions: ["Active", "Q Suspension", "Terminated"]
          }),
        };
      })
    );
    setColumnVisibility(websiteColumnVisibility);
  }, [websiteColumnVisibility]);

  const visibleColumns = columns.filter((column) =>
    columnVisibility.find((col) => col.columnName === column.field)?.visibility
  );

  const handleResize = (field, newWidth) => {
    const updatedColumns = columns.map((col) =>
      col.field === field ? {...col, width: newWidth} : col
    );
    setColumns(updatedColumns);

    const updatedVisibility = columnVisibility.map((col) =>
      col.columnName === field ? {...col, width: newWidth} : col
    );
    setColumnVisibility(updatedVisibility);

    return updatedVisibility;
  };

  const commitResize = (updatedVisibility) => {
    setUserPreference({newWebsiteColumnVisibility: updatedVisibility});
  };

  const handleInActive = async () => {
    const newHideInActive = !hideInActive;
    await setUserPreference({
      websiteColumnVisibility: columnVisibility,
      hideInActive: newHideInActive.toString(),
    });
    setHideInActive(newHideInActive);
  };

  const handleDenied = async () => {
    const newHideDenied = !hideDenied;
    await setUserPreference({
      websiteColumnVisibility: columnVisibility,
      hideDenied: newHideDenied.toString(),
    });
    setHideDenied(newHideDenied);
  };

  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));

    const fieldMapping = {
      Vendor: "vendor",
      "Domain Name": "domainName",
      Licensee: "licensee",
      "First Name": "firstName",
      "Last Name": "lastName",
      Status: "status",
      "MLS Status": "mlsStatus",
      Office: "office",
      "MLS ID": "mlsId",
      "Member Number": "memberNumber",
      "License Number": "licenseNumber",
      "Start Date": "startDate",
      "Term Date": "termDate",
      "Vendor Code": "vendorCode",
      Reciprocal: "reciprocal",
      DBA: "dba",
      "Member AOR": "memberAOR",
      "Documents Waiting": "documentsWaiting",
      "Voided Documents": "voidedDocuments",
      "Broker First Name": "broker.firstName",
      "Broker Last Name": "broker.lastName",
      "Broker MLS ID": "broker.mlsId",
      "Broker Member Number": "broker.memberNumber",
      "Broker Office": "broker.office",
      "Broker Office ID": "broker.officeId",
      "Broker License Number": "broker.licenseNumber",
      "Broker Status": "broker.status",
    };

    try {
      const fromYear = startDate ? parseInt(startDate, 10) : null;
      const untilYear = termDate ? parseInt(termDate, 10) : null;

      if (fromYear && (fromYear < 1900 || fromYear > new Date().getFullYear())) {
        console.error("Invalid From Year");
        alert("Invalid From Year. Please enter a year between 1900 and the current year.");
        setIsExporting(false);
        return;
      }
      if (untilYear && (untilYear < 1900 || untilYear > new Date().getFullYear())) {
        console.error("Invalid Until Year");
        alert("Invalid Until Year. Please enter a year between 1900 and the current year.");
        setIsExporting(false);
        return;
      }

      let exportData = [...data].filter((item) => {
        const startDateValue = item.startDate;
        const termDateValue = item.termDate;

        if (!startDateValue) {
          return false;
        }

        let startYear = null;
        let termYear = null;

        try {
          const date = new Date(startDateValue);
          startYear = date.getFullYear();
        } catch (error) {
          console.warn(`Invalid start date format for item: ${startDateValue}`);
          return false;
        }

        if (termDateValue) {
          try {
            const date = new Date(termDateValue);
            termYear = date.getFullYear();
          } catch (error) {
            console.warn(`Invalid term date format for item: ${termDateValue}`);
            return false;
          }
        }

        if (!fromYear && !untilYear) {
          return true;
        }

        if (fromYear && untilYear) {
          if (startYear < fromYear || startYear > untilYear) {
            return false;
          }
          if (termYear && termYear < fromYear) {
            return false;
          }
          return true;
        }

        if (fromYear && !untilYear) {
          if (startYear < fromYear) return false;
          if (termYear && termYear < fromYear) return false;
          return true;
        }

        if (!fromYear && untilYear) {
          if (startYear > untilYear) return false;
          if (termYear && termYear > untilYear) return false;
          return true;
        }

        return false;
      });

      const formattedData = exportData.map((item) => {
        const exportItem = {};

        Object.entries(fieldMapping).forEach(([label, fieldPath]) => {
          if (exportOptions[fieldPath.split('.')[0]] ||
            (fieldPath.startsWith('broker.') && exportOptions.broker[fieldPath.split('.')[1]])) {
            const value = fieldPath.includes(".")
              ? fieldPath.split(".").reduce((acc, key) => acc?.[key], item)
              : item[fieldPath];

            if (label === "Status") {
              const fallbackValue = item.Status || value;
              exportItem[label] = fallbackValue !== undefined && fallbackValue !== null
                ? fallbackValue
                : "N/A";
            } else {
              exportItem[label] = value !== undefined && value !== null ? value : "N/A";
            }
          }
        });

        return exportItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Export");

      const buffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
      const blob = new Blob([buffer], {type: "application/octet-stream"});

      saveAs(blob, "WebsiteTable.xlsx");
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      alert("An error occurred during export. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDropdownClick = (e, identifier) => {
    e.stopPropagation();
    setOpenDropdownIndex((prevIndex) =>
      prevIndex === identifier ? null : identifier
    );
  };

  const handleWebsiteClick = async (id) => {
    try {
      const website = await getWebsite(id);
      setSelectedWebsite(website);
      navigate(`/websites/${id}`, {replace: true});
      openModal();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const match = location.pathname.match(/\/websites\/(\d+)/);
    if (match) {
      const id = match[1];
      (async () => {
        const website = await getWebsite(id);
        setSelectedWebsite(website);
        openModal();
      })();
    }
  }, [location.pathname]);

  const handleNewWebsiteClick = () => {
    setSelectedWebsite(null);
    openModal();
  };

  const {items, requestSort, sortConfig} = useSortableData(data);

  const filteredItems = useMemo(() => {
    let itemsToFilter = items;

    if (hideInActive) {
      itemsToFilter = itemsToFilter.filter((item) => item.Status !== "Inactive");
    }
    if (hideDenied) {
      itemsToFilter = itemsToFilter.filter((item) => item.Status !== "Denied");
    }
    if (searchTerm) {
      itemsToFilter = itemsToFilter.filter((item) =>
        Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    Object.keys(checkboxStatus).forEach((key) => {
      const filterValues = checkboxStatus[key];
      if (filterValues && filterValues.length > 0) {
        let mappedKey = headerToKeyMapping[key] || headerToKeyMapping[key.replace(/([A-Z])/g, " $1").trim()];
        if (!mappedKey) {
          const column = columns.find((col) => col.header === key);
          mappedKey = column ? column.field : key.toLowerCase();
          console.warn(`No mapping found for header "${key}" in headerToKeyMapping. Using "${mappedKey}" as fallback.`);
        }

        itemsToFilter = itemsToFilter.filter((item) => {
          const itemValue = item[mappedKey];
          const matches = itemValue !== undefined && itemValue !== null && filterValues.includes(itemValue);

          console.log(`Item[${mappedKey}] = "${itemValue}", Matches: ${matches}`);
          return matches;
        });
      }
    });

    itemsToFilter = itemsToFilter.filter((item) => {
      return Object.entries(iconFilters).every(([key, value]) => {
        if (!value) return true;
        if (key === "hasReciprocal") {
          return item.reciprocal === true;
        }
        return item[key] === true;
      });
    });

    return itemsToFilter;
  }, [
    items,
    searchTerm,
    checkboxStatus,
    hideInActive,
    hideDenied,
    iconFilters,
    headerToKeyMapping,
    columns,
  ]);

  const summaryData = useMemo(() => {
    const totalInactive = filteredItems.filter(
      (item) => item.Status === "Inactive"
    ).length;
    const totalActive = filteredItems.filter(
      (item) => item.Status === "Active"
    ).length;
    const totalPending = filteredItems.filter(
      (item) => item.Status === "Pending Activation"
    ).length;

    return {
      total: filteredItems.length.toLocaleString(),
      active: totalActive.toLocaleString(),
      inactive: totalInactive.toLocaleString(),
      pending: totalPending.toLocaleString(),
    };
  }, [filteredItems]);

  useEffect(() => {
    const handleResize = () => {
      setDynamicHeight(window.innerHeight - 203);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      {threshold: 1}
    );
    if (loaderRef.current && !loading) {
      observer.observe(loaderRef.current);
    }
    return () => observer.disconnect();
  }, [loading, setPage]);

  useEffect(() => {
    getAllWebsites().then(() => setIsInitialLoad(false));
  }, [getAllWebsites])

  useEffect(() => {
    const tl = gsap.timeline({repeat: -1, repeatDelay: 0.5});
    tl.to(loaderRef.current, {x: "+=200", duration: 1})
      .to(textRef.current, {x: "400", opacity: 0, duration: 0.1}, "-=0.5")
      .to(loaderRef.current, {x: "-=200", duration: 1})
      .to(textRef.current, {x: "400", opacity: 1, duration: 0.1}, "-=0.5");
    return () => {
      tl.kill();
    };
  }, []);

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const openDomainInNewTab = (domainName) => {
    let url = domainName;
    if (!domainName.startsWith("http://") && !domainName.startsWith("https://")) {
      url = "http://" + domainName;
    }
    window.open(url, "_blank");
  };

  const tableWidth = '100%';

  const rowHeight = 50;

  const Row = ({index, style}) => {
    const item = filteredItems[index];
    const isInactive = item.Status === "Inactive";

    return (
      <div
        style={{
          ...style,
          borderBottom: darkMode
            ? "1px solid rgba(120, 113, 108, 0.4)"
            : "1px solid rgba(128, 128, 128, 0.2)"
        }}
        className={`flex hover:bg-gray-100 dark:hover:bg-stone-900 cursor-pointer ${
          isInactive ? "text-gray-400 dark:text-gray-500" : ""
        }`}
        onClick={() => handleWebsiteClick(item.id)}
      >

        {visibleColumns.map((column, colIndex) => (
          <div
            key={colIndex}
            className="cell text-xs px-4 py-4 whitespace-nowrap"
            style={{width: column.width}}
          >
            <div
              title={capitalizeFirstLetter(item[column.field]) || "N/A"}
              style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
            >
              {column.field === "vendor" && (
                <div style={{display: "flex", alignItems: "center"}}>
                  {item[column.field] || "N/A"}
                  {item.reciprocal === true && (
                    <img
                      title="Reciprocal"
                      src={recip}
                      alt="Reciprocal"
                      className="h-4 w-4 recip"
                      style={{marginLeft: "10px"}}
                    />
                  )}
                  {item.documentsWaiting && (
                    <FaExclamationCircle
                      title="Pending Document"
                      className="doc-waiting-icon text-orange-400 dark:text-yellow-400 h-4 w-4 ml-3"
                    />
                  )}
                  {item.voidedDocuments && (
                    <FaBan
                      title="Voided Document"
                      className="doc-voided-icon text-red-400 dark:text-red-400 h-4 w-4 ml-3"
                    />
                  )}
                </div>
              )}
              {column.field !== "vendor" &&
                column.field !== "domainName" &&
                column.field !== "Status" &&
                (item[column.field] || "N/A")}
              {column.field === "domainName" && (
                <div className="flex justify-between items-center w-full">
                  <span
                    className="truncate underline font-bold"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDomainInNewTab(item[column.field] || "N/A");
                    }}
                  >
                    {item[column.field] || "N/A"}
                  </span>
                </div>
              )}
              {column.field === "Status" && (
                <span
                  className={`inline-block ${
                    item[column.field] === "Pending Termination"
                      ? "px-2.5 py-1 rounded-md text-red-500 bg-red-100 dark:text-white dark:bg-red-500"
                      : item[column.field] === "Pending Activation"
                        ? "px-3.5 py-1 rounded-md text-blue-500 bg-blue-100 dark:text-white dark:bg-blue-500"
                        : item[column.field] === "Pending Change"
                          ? "px-3.5 py-1 rounded-md text-gray-500 bg-gray-100 dark:text-white dark:bg-gray-500 min-w-[129px] text-center"
                          : "px-3.5 py-1 rounded-md"
                  }`}
                >
                  {item[column.field] || "N/A"}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const FilterDropdown = ({index}) => {
    const isOpen = index === openDropdownIndex;
    const column = visibleColumns[index];
    const filterOptions = column?.filterOptions || [];
    const {header} = column;

    const handleFilterOptionClick = (event, option) => {
      event.stopPropagation();
      setCheckboxStatus((prevStatus) => {
        const newStatus = {...prevStatus};
        if (!newStatus[header]) newStatus[header] = [];
        if (newStatus[header].includes(option)) {
          newStatus[header] = newStatus[header].filter((item) => item !== option);
        } else {
          newStatus[header].push(option);
        }
        return newStatus;
      });
    };

    const handleResetFilter = (event) => {
      event.stopPropagation();
      setCheckboxStatus((prevStatus) => {
        const newStatus = {...prevStatus};
        delete newStatus[header];
        return newStatus;
      });
      setOpenDropdownIndex(null);
    };

    if (!isOpen) return null;

    return (
      <div
        ref={dropdownRef}
        className="absolute z-50 bg-white dark:bg-stone-900 p-3 rounded drop-shadow-2xl w-full"
        style={{top: "calc(100% + 0.5rem)", minWidth: "10rem", zIndex: 50}}
      >
        <div className="flex flex-col space-y-1 w-full">
          {filterOptions.map((option, i) => (
            <label
              key={i}
              className="flex items-center text-left space-x-2"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="form-checkbox"
                checked={checkboxStatus[header]?.includes(option) || false}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterOptionClick(e, option);
                }}
              />
              <span className="text-gray-500 dark:text-gray-300 w-full text-xs">
                {option}
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-left mt-4">
          <button
            className="bg-gray-500 w-1/2 hover:bg-gray-600 text-white font-semibold px-2 py-1 rounded"
            onClick={handleResetFilter}
          >
            Reset
          </button>
        </div>
      </div>
    );
  };

  const dropdownRef = useRef(null);
  const columnDropdownRef = useRef(null);

  const handleColumnToggle = (field) => {
    const updatedVisibility = columnVisibility.map((col) =>
      col.columnName === field ? {...col, visibility: !col.visibility} : col
    );
    setColumnVisibility(updatedVisibility);
    setUserPreference({newWebsiteColumnVisibility: updatedVisibility});
  };

  const ColumnsDropdown = () => {
    const isOpen = openDropdownIndex === "columns";

    if (!isOpen) return null;

    return (
      <div
        ref={columnDropdownRef}
        className="absolute z-50 left-24 bg-white dark:bg-stone-700 border border-gray-200 dark:border-stone-600 rounded shadow-lg p-3"
        style={{top: "calc(100% + 0.5rem)", minWidth: "15rem", textAlign: "left"}}
      >
        <div className="flex flex-col space-y-1 w-full">
          {columns.map((column, i) => (
            <label
              key={i}
              className="flex items-center space-x-2 p-1"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                checked={columnVisibility.find(col => col.columnName === column.field)?.visibility}
                onChange={() => handleColumnToggle(column.field)}
              />
              <span className="text-sm text-gray-900 dark:text-gray-300 w-full">
                {column.header}
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (dropdownRef.current && !dropdownRef.current.contains(event.target)) ||
        (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target))
      ) {
        setOpenDropdownIndex(null);
        triggerDoubleClickRequirement();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownIndex]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setCheckboxStatus({});
    setIconFilters({
      actionNeeded: false,
      documentsWaiting: false,
      voidedDocuments: false,
      hasReciprocal: false,
      reciprocalAllowed: false,
      masterLinkedVendor: false,
      linkedVendor: false,
    });
    setIsFilterActive(false);
  };

  useEffect(() => {
    const hasActiveCheckbox = Object.values(checkboxStatus).some(
      (arr) => arr.length > 0
    );
    const iconFilterActive = Object.values(iconFilters).some(
      (value) => value
    );
    const active =
      hasActiveCheckbox || searchTerm.length > 0 || iconFilterActive;
    setIsFilterActive(active);
  }, [checkboxStatus, searchTerm, iconFilters]);

  const tableContainerRef = useRef(null);
  const isPanning = useRef(false);
  const startPos = useRef({x: 0, y: 0});

  const onMouseDown = (e) => {
    if (e.button === 1) {
      isPanning.current = true;
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
      };
      e.preventDefault();
    }
  };

  const onMouseUp = (e) => {
    if (e.button === 1) {
      isPanning.current = false;
    }
  };

  const onMouseMove = (e) => {
    if (isPanning.current) {
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
      };
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollLeft += dx;
        tableContainerRef.current.scrollTop -= dy;
      }
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("mousedown", onMouseDown);
      container.addEventListener("mouseup", onMouseUp);
      container.addEventListener("mousemove", onMouseMove);
    }

    return () => {
      if (container) {
        container.removeEventListener("mousedown", onMouseDown);
        container.removeEventListener("mouseup", onMouseUp);
        container.removeEventListener("mousemove", onMouseMove);
      }
    };
  }, []);

  return (
    <div className="shadow sm:rounded-lg bg-white dark:bg-stone-800 min-w-full">
      <div className="header p-2 flex justify-between bg-gray-400 dark:bg-stone-800">
        <div className="search-section flex items-center">
          <input
            type="text"
            className="search-input border-2 border-gray-300 bg-white h-7 px-5 pr-10 rounded-md text-sm focus:outline-none dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const lowerSearch = searchTerm.trim().toLowerCase();
                if (lowerSearch === "tetris:start") {
                  setShowTetrisModal(true);
                  setSearchTerm("");
                } else if (['rickroll', 'rick roll', 'never going to give you up', 'never gonna give you up', 'never gonna give'].includes(lowerSearch)) {
                  setShowRickRoll(true);
                  setSearchTerm("");
                }
              }
            }}
            placeholder="Search..."
          />
          {showTetrisModal && (
            <TetrisModal isOpen={showTetrisModal} onClose={() => setShowTetrisModal(false)}/>
          )}
          {showRickRoll && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-stone-900 rounded-lg p-4 w-1/4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-600 dark:text-gray-100">Never Gonna Give You Up</h2>
                  <button onClick={() => setShowRickRoll(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>
                <iframe
                  width="100%"
                  height="315"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                  title="Rick Roll"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}
          <div className="summary ml-4 flex">
            <div className="relative group">
              <div
                className="summary-label select-none text-sm font-semibold text-gray-50"
                style={{minWidth: "70px"}}
              >
                Total: {summaryData.total}
              </div>
              <div
                className="summary-content absolute left-0 mt-2 w-48 z-50 rounded-md shadow-lg bg-white dark:bg-stone-600 dark:text-gray-300 ring-1 ring-black ring-opacity-5 overflow-hidden hidden group-hover:block"
              >
                <div className="py-1 select-none">
                  <span className="summary-item block px-4 py-2 text-sm">
                    Active: {summaryData.active}
                  </span>
                  <span className="summary-item block px-4 py-2 text-sm">
                    Inactive: {summaryData.inactive}
                  </span>
                  <span className="summary-item block px-4 py-2 text-sm">
                    Pending: {summaryData.pending}
                  </span>
                </div>
              </div>
            </div>
            <div className="legend-section relative ml-3 flex items-center">
              <div className="filter-icon-container" style={{width: "35px"}}>
                {isFilterActive && (
                  <MdFilterAltOff
                    className="mt-0.5 mr-2 text-gray-300 cursor-pointer"
                    onClick={clearAllFilters}
                  />
                )}
              </div>
              <div
                className="legend-trigger flex items-center cursor-pointer"
                onClick={toggleLegend}
              >
                <span className="text-sm font-semibold text-gray-50 mr-2">
                  Icons
                </span>
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {showLegend && (
                <div
                  ref={legendRef}
                  className="legend-tooltip absolute top-full mt-2 left-0 p-2 bg-white dark:bg-stone-700 border border-gray-200 dark:border-stone-600 rounded shadow-lg z-50"
                  style={{minWidth: "15rem", textAlign: "left"}}
                >
                  {Object.entries(iconConfigurations).map(
                    ([key, {icon, label}]) => (
                      <label key={key} className="flex items-center space-x-2 p-1">
                        <input
                          type="checkbox"
                          name="filterGroup"
                          checked={iconFilters[key]}
                          onChange={() => toggleFilter(key)}
                          className="form-radio rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-300 flex items-center">
                          {icon}
                          {label}
                        </span>
                      </label>
                    )
                  )}
                </div>
              )}
              <div
                className="columns-trigger flex items-center cursor-pointer ml-4"
                onClick={(e) => handleDropdownClick(e, "columns")}
              >
                <span className="text-sm font-semibold text-gray-50 mr-2">
                  Columns
                </span>
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {openDropdownIndex === "columns" && <ColumnsDropdown/>}
            </div>
          </div>
        </div>
        <div className="flex">
          {hideInActive && (
            <button
              onClick={handleInActive}
              className="new-website-btn mr-2 rounded px-1 font-semibold text-gray-900 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100"
              title="Show Inactive"
            >
              Show Inactive
            </button>
          )}
          {hideDenied && (
            <button
              onClick={handleDenied}
              className="new-website-btn mr-2 rounded px-1 font-semibold text-gray-900 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100"
              title="Show Denied"
            >
              Show Denied
            </button>
          )}
          <button
            onClick={handleNewWebsiteClick}
            className="new-website-btn rounded bg-blue-600 px-2 font-semibold text-white hover:bg-blue-500 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            New Website
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="export-button ml-2 rounded bg-gray-500 px-4 font-semibold text-white hover:bg-gray-600"
          >
            <img
              src={exportIcon}
              alt="Export"
              className="mb-1 mr-1 inline-block h-4 w-4"
            />
            Export
          </button>
          {role === 'Admin' && (
            <button
              onClick={onSwitchToImports}
              className="import-button ml-2 max-h-8 flex items-center gap-2 rounded bg-gray-500 px-4 font-semibold text-white hover:bg-gray-600"
            >
              <LuImport className="mt-1 h-[18px] w-[18px]"/>
              Import
            </button>
          )}
        </div>
      </div>
      <div
        ref={tableContainerRef}
        className="website-table-container max-h-screen overflow-auto w-full"
      >
        <table className="data-table min-w-full divide-y divide-gray-200 dark:divide-stone-700">
          <thead className="z-40 bg-gray-500 dark:bg-stone-600 sticky top-0 shadow-md">
          <tr className="flex">
            {visibleColumns.map((column, index) => (
              <th
                key={index}
                className={`header-item flex items-center select-none justify-start px-4 py-2 text-xs font-medium text-white uppercase tracking-wider transition-all duration-200 ease-in-out transform ${
                  index === 0 ? "first-header-item mr-2" : ""
                } ${index === visibleColumns.length - 1 ? "last-header-item" : ""}`}
                style={{
                  width: column.width || "auto",
                  position: "relative",
                }}
              >
                <span>{column.header}</span>
                <div
                  className="sort-icons flex items-center ml-2"
                  style={{alignItems: "center", cursor: "default"}}
                >
                  <span
                    className={`sort-icon ${
                      sortConfig?.key === column.field && sortConfig?.direction === "ascending"
                        ? "text-white font-bold"
                        : "text-gray-400"
                    }`}
                    style={{fontSize: "0.6rem", cursor: "pointer"}}
                    onClick={() => requestSort(column.field)}
                  >
                    ▲
                  </span>
                  <span
                    className={`sort-icon ${
                      sortConfig?.key === column.field && sortConfig?.direction === "descending"
                        ? "text-white font-bold"
                        : "text-gray-400"
                    }`}
                    style={{fontSize: "0.6rem", cursor: "pointer"}}
                    onClick={() => requestSort(column.field)}
                  >
                    ▼
                  </span>
                  {column.filter && (
                    <div
                      className="filter-icon ml-1 relative"
                      onClick={(e) => handleDropdownClick(e, index)}
                    >
                      <FilterIcon className="h-2.5 w-2.5"/>
                      {openDropdownIndex === index && <FilterDropdown index={index}/>}
                    </div>
                  )}
                </div>
                {index < visibleColumns.length - 1 && (
                  <div
                    className="absolute right-0 top-0 h-full w-[15px] cursor-col-resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startWidth = column.width || 160;

                      let latestVisibility = columnVisibility;

                      const onMouseMove = (moveEvent) => {
                        const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
                        latestVisibility = handleResize(column.field, newWidth);
                      };

                      const onMouseUp = () => {
                        document.removeEventListener("mousemove", onMouseMove);
                        document.removeEventListener("mouseup", onMouseUp);
                        commitResize(latestVisibility);
                      };

                      document.addEventListener("mousemove", onMouseMove);
                      document.addEventListener("mouseup", onMouseUp);
                    }}
                  >
                    <div
                      className="absolute right-0 top-0 h-full w-[2px] bg-white dark:bg-stone-700"
                      style={{cursor: "col-resize"}}
                    ></div>
                  </div>
                )}
              </th>
            ))}
          </tr>
          </thead>
          <tbody
            className="table-body z-10 bg-white divide-y divide-gray-200 dark:bg-stone-800 dark:divide-stone-700 dark:text-gray-300">
          {isInitialLoad && data.length === 0 ? (
            <tr className="w-full bg-gray-100 dark:bg-stone-900" style={{height: dynamicHeight}}>
              <td colSpan={visibleColumns.length}>
                <div className="flex justify-center items-center h-full">
                  <FaSpinner className="animate-spin h-6 w-6 text-blue-600 dark:text-indigo-600 mr-2"/>
                  <p className="font-semibold text-blue-600 dark:text-indigo-600">Loading...</p>
                </div>
              </td>
            </tr>
          ) : filteredItems.length > 0 ? (
            <List height={dynamicHeight} itemCount={filteredItems.length} itemSize={rowHeight} width={tableWidth}>
              {Row}
            </List>
          ) : (
            <tr className="w-full bg-gray-100 dark:bg-stone-900" style={{height: dynamicHeight}}>
              <td colSpan={visibleColumns.length}>
                <div className="flex justify-center items-center h-full">
                  <p className="font-semibold text-gray-600 dark:text-gray-400">No Results</p>
                </div>
              </td>
            </tr>
          )}
          </tbody>
        </table>
        {loading && filteredItems.length > 0 && (
          <div className="flex justify-center items-center py-4">
            <FaSpinner className="animate-spin h-6 w-6 text-blue-600 dark:text-indigo-600 mr-2"/>
            <p className="font-semibold text-blue-600 dark:text-indigo-600">Loading more...</p>
          </div>
        )}
      </div>
      {isOpen && (
        <WebsiteModal onClose={closeModal} website={selectedWebsite}/>
      )}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Export Data"
        >
          <div className="flex flex-col space-y-4 py-2 pl-2 pr-16">
            <div className="grid grid-cols-5 gap-4">
              {Object.entries(exportOptions)
                .filter(([key]) => key !== "broker")
                .map(([key, value]) => (
                  <label key={key} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name={key}
                      checked={value}
                      onChange={(e) =>
                        setExportOptions({
                          ...exportOptions,
                          [e.target.name]: e.target.checked,
                        })
                      }
                      className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      disabled={isExporting}
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                      {exportKeyLabels[key]}
                    </span>
                  </label>
                ))}
            </div>
            <div className="grid grid-cols-5 gap-4 mt-4">
              {Object.entries(exportOptions.broker).map(([key, value]) => (
                <label
                  key={`broker-${key}`}
                  className="flex items-center space-x-2"
                >
                  <input
                    type="checkbox"
                    name={key}
                    checked={value}
                    onChange={(e) =>
                      setExportOptions({
                        ...exportOptions,
                        broker: {
                          ...exportOptions.broker,
                          [e.target.name]: e.target.checked,
                        },
                      })
                    }
                    className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    disabled={isExporting}
                  />
                  <span className="text-gray-700 dark:text-gray-200">
                    {exportKeyLabels.broker[key]}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end items-center space-x-4">
              <label className="block">
                <span className="text-gray-700 dark:text-gray-200">
                  From Year:
                </span>
                <input
                  type="number"
                  value={startDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 4) setStartDate(value);
                  }}
                  className="mt-1 block w-36 rounded-md px-2 py-1.5 bg-gray-100 border border-gray-300 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{WebkitAppearance: "none", MozAppearance: "textfield"}}
                  disabled={isExporting}
                />
              </label>
              <label className="block">
                <span className="text-gray-700 dark:text-gray-200">
                  Until Year:
                </span>
                <input
                  type="number"
                  value={termDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 block w-36 rounded-md px-2 py-1.5 bg-gray-100 border border-gray-300 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{WebkitAppearance: "none", MozAppearance: "textfield"}}
                  disabled={isExporting}
                />
              </label>
            </div>
            <div className="flex justify-end space-x-2 pt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black font-bold rounded focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 disabled:opacity-50"
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-700 text-white font-bold rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isExporting ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-3.5 w-3.5"/>
                    <span>Exporting...</span>
                  </div>
                ) : (
                  "Export"
                )}
              </button>
            </div>
          </div>
        </ExportModal>
      )}
    </div>
  );
};

export default WebsiteTable;